import type { Lesson } from "@/core/types";
import { formelAufgabe } from "../formelaufgabe";

export const lektionDatentabelle: Lesson = {
  id: "datentabelle",
  title: "Datentabelle und Prognoseblatt",
  summary: "Ein Ergebnis für viele Eingabewerte auf einmal – und ein Blick in die Zukunft.",
  durationMinutes: 11,
  learningGoals: [
    "Eine Datentabelle mit einer Variablen anlegen",
    "Ein Prognoseblatt aus einer Zeitreihe erzeugen",
  ],
  steps: [
    {
      id: "mehrfachoperation",
      title: "Die Datentabelle",
      explanation: [
        "Die Datentabelle beantwortet „Wie hoch ist die Rate bei 3, 4, 5 und 6 % Zins?“ in " +
          "einem Schritt. Aufbau: In eine Spalte untereinander die Zinssätze, rechts daneben " +
          "eine Zeile höher ein Verweis auf die Ergebnisformel (=B4). Den ganzen Block " +
          "markieren, „Daten“ → „Was-wäre-wenn-Analyse“ → „Datentabelle“, „Werte aus Spalte“ " +
          "= die Zinszelle B2.",
        "Excel füllt die Ergebnisse mit der Formel {=MEHRFACHOPERATION(;B2)}. Sie lässt sich " +
          "nicht einzeln ändern – nur als Ganzes löschen. Mit zwei Variablen (Zins und " +
          "Laufzeit) entsteht eine Matrix.",
        "Datentabellen rechnen bei jeder Änderung neu und können große Mappen bremsen. " +
          "Dafür gibt es unter „Formeln“ → „Berechnungsoptionen“ die Einstellung " +
          "„Automatisch außer bei Datentabellen“.",
      ],
      keyPoints: [
        "Eingabewerte untereinander, Ergebnisverweis eine Zeile höher rechts.",
        "Daten → Was-wäre-wenn-Analyse → Datentabelle → Werte aus Spalte.",
        "MEHRFACHOPERATION nur als Ganzes löschbar.",
      ],
      task: {
        instruction: "Lege für dein Kreditmodell eine Datentabelle an, die die Rate für mindestens vier Zinssätze zeigt.",
        hint: "Zinssätze in D3:D6, in E2 =B4, dann D2:E6 markieren und Datentabelle → Werte aus Spalte: B2.",
        check: formelAufgabe(
          [{ label: "Eine Datentabelle (MEHRFACHOPERATION)", funktionen: ["mehrfachoperation"] }],
          {
            success: "Vier Varianten auf einen Blick – und sie rechnen mit, wenn sich das Modell ändert.",
            failure: "Es gibt noch keine Datentabelle.",
            hint: "Die Formel MEHRFACHOPERATION entsteht nur über den Dialog, nicht durch Tippen.",
          },
        ),
      },
    },
    {
      id: "prognoseblatt",
      title: "Das Prognoseblatt",
      explanation: [
        "Liegen Werte über die Zeit vor – Monatsumsätze, Besucherzahlen –, erzeugt " +
          "„Daten“ → „Prognoseblatt“ eine Vorhersage samt Diagramm und Konfidenzintervall. " +
          "Voraussetzung sind gleichmäßige Zeitabstände in einer Datums- und eine " +
          "Wertspalte.",
        "Das neue Blatt enthält Formeln mit PROGNOSE.ETS. Sie berücksichtigen Trend und " +
          "Saisonalität. Eine Prognose ist trotzdem nur so gut wie die Vergangenheit, auf " +
          "der sie beruht – das Konfidenzintervall zeigt, wie unsicher sie ist.",
      ],
      keyPoints: [
        "Daten → Prognoseblatt.",
        "Gleichmäßige Zeitabstände sind Voraussetzung.",
        "Das Konfidenzintervall zeigt die Unsicherheit.",
      ],
      task: {
        instruction: "Erzeuge aus mindestens acht Monatswerten ein Prognoseblatt.",
        hint: "Datum und Wert in zwei Spalten, beides markieren, Daten → Prognoseblatt → Erstellen.",
        check: formelAufgabe([{ label: "Eine Prognoseformel (PROGNOSE.ETS)", funktionen: ["prognose"] }], {
          success: "Die Prognose steht – samt Unsicherheitsbereich.",
          failure: "Es gibt noch keine Prognose.",
          hint: "Meldet Excel einen Fehler, sind die Datumsabstände nicht gleichmäßig.",
        }),
      },
    },
  ],
};
